// =============================================================================
//  MISSIONS AGENTIC — l'écran qui rend la chaîne REGARDABLE
// =============================================================================
//  🔴 CE QU'IL RÉPARE. `agentic_missions` et `agentic_executions` existaient en
//  base, l'orchestrateur tournait, la preuve était écrite — et RIEN ne
//  l'exposait. Mesuré le 06/09 : aucune route, aucun écran. ⇒ Un résultat en
//  base qu'on ⛔ ne peut PAS regarder n'est pas un livrable.
//
//  🔑 LE SEUL CRITÈRE : en 10 secondes, voir CE QUI TOURNE, CE QUI BLOQUE, CE
//  QUI EST FINI, et S'IL FAUT INTERVENIR. ⛔ Pas un tableau de bord décoratif —
//  d'où quatre compteurs, une liste, un détail. Rien d'autre.
//
//  ⛔ LECTURE SEULE, sauf l'escalade, déjà supportée par l'API. ⛔ Aucun nouveau
//  modèle, aucune nouvelle route, aucune logique de clôture ici.
//
//  ⚠️ IL DIT CE QUI N'EXISTE PAS ENCORE. L'escalade POSE l'état et ⛔ ne SUSPEND
//  RIEN : le mécanisme d'attente (signal, reprise) n'existe pas. C'est écrit à
//  l'écran, ⛔ pas seulement dans le code — l'utilisateur doit comprendre ce
//  qu'il voit ET ce qui viendra.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, Hand,
         PlayCircle, ChevronRight, ArrowLeft, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const BASE = `${API}/api/agentic/missions`;

// ⚠️ Paires fond/texte dual-mode. ⛔ Jamais une couleur en dur : en mode clair,
// un texte clair sur fond clair de la même famille devient illisible.
const TONS = {
  ouverte:        { bg: "var(--oh-stat-blue-bg)",   text: "var(--oh-stat-blue-text)",   libelle: "Ouverte" },
  en_cours:       { bg: "var(--oh-stat-amber-bg)",  text: "var(--oh-stat-amber-text)",  libelle: "En cours" },
  terminee:       { bg: "var(--oh-stat-green-bg)",  text: "var(--oh-stat-green-text)",  libelle: "Terminée" },
  bloquee:        { bg: "var(--oh-stat-red-bg)",    text: "var(--oh-stat-red-text)",    libelle: "Bloquée" },
  attente_humain: { bg: "var(--oh-stat-purple-bg)", text: "var(--oh-stat-purple-text)", libelle: "Attente humain" },
};

const carte = {
  background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
  borderRadius: 12, padding: 16,
};
const etiquette = { fontSize: 12, color: "var(--oh-text-secondary)", fontWeight: 500 };
const btnDiscret = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
  borderRadius: 8, fontSize: 12.5, cursor: "pointer", background: "transparent",
  border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)",
};

function dateFR(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("fr-FR",
    { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function Badge({ statut }) {
  const t = TONS[statut] || { bg: "var(--oh-bg-surface)",
                              text: "var(--oh-text-secondary)",
                              libelle: statut || "—" };
  return (
    <span style={{ background: t.bg, color: t.text, borderRadius: 8,
                   padding: "2px 8px", fontSize: 12, fontWeight: 500,
                   whiteSpace: "nowrap" }}>
      {t.libelle}
    </span>
  );
}

/** Les quatre chiffres qui répondent à la question en 10 secondes.
 *  ⛔ Pas un de plus : chaque compteur supplémentaire retarde la lecture. */
function Compteurs({ missions }) {
  const c = useMemo(() => ({
    tourne: missions.filter((m) => m.statut === "en_cours" || m.statut === "ouverte").length,
    bloque: missions.filter((m) => m.statut === "bloquee").length,
    fini: missions.filter((m) => m.statut === "terminee").length,
    // 🔑 « À moi » = l'état d'attente OU le besoin déclaré à la création.
    // ⛔ Ne compter que l'état raterait une mission qui a annoncé dès le départ
    // qu'elle aurait besoin d'un humain.
    moi: missions.filter((m) => m.statut === "attente_humain" || m.besoin_hitl).length,
  }), [missions]);

  const cases = [
    { cle: "tourne", libelle: "Tourne", n: c.tourne, icone: PlayCircle, ton: "blue" },
    { cle: "bloque", libelle: "Bloqué", n: c.bloque, icone: AlertTriangle, ton: "red" },
    { cle: "fini", libelle: "Fini", n: c.fini, icone: CheckCircle2, ton: "green" },
    { cle: "moi", libelle: "À moi", n: c.moi, icone: Hand, ton: "purple" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 12, marginBottom: 24 }}>
      {cases.map(({ cle, libelle, n, icone: I, ton }) => (
        <div key={cle} style={{ ...carte, display: "flex", alignItems: "center", gap: 12 }}>
          <I size={20} style={{ color: `var(--oh-stat-${ton}-text)`, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 22, fontWeight: 500, color: "var(--oh-text)",
                          fontVariantNumeric: "tabular-nums" }}>{n}</div>
            <div style={etiquette}>{libelle}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** ⚠️ L'avertissement que le CTO a demandé : l'écran dit ce qui N'EXISTE PAS.
 *  ⛔ Sans lui, « Escalader » laisserait croire que l'exécution s'arrête. */
function AvertissementHITL() {
  return (
    <div style={{ ...carte, background: "var(--oh-stat-amber-bg)", padding: 12,
                  display: "flex", gap: 10, alignItems: "flex-start", marginTop: 16 }}>
      <Info size={16} style={{ color: "var(--oh-stat-amber-text)", flexShrink: 0, marginTop: 2 }} />
      <div style={{ fontSize: 12.5, color: "var(--oh-stat-amber-text)", lineHeight: 1.6 }}>
        <strong>Escalader pose l'état, mais ne suspend rien.</strong> Le mécanisme
        d'attente humaine n'existe pas encore : l'exécution en cours continue.
        La mission sera marquée « Attente humain » pour être retrouvée, c'est tout.
      </div>
    </div>
  );
}

function Detail({ mission, onRetour, onEscalade, occupe }) {
  const wi = mission.work_items || [];
  const ex = mission.executions || [];
  return (
    <div>
      <button style={{ ...btnDiscret, marginBottom: 16 }} onClick={onRetour}
              aria-label="Revenir à la liste des missions">
        <ArrowLeft size={14} /> Toutes les missions
      </button>

      <div style={{ ...carte, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 15, color: "var(--oh-text)", lineHeight: 1.6 }}>
            {mission.objectif}
          </div>
          <Badge statut={mission.statut} />
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12.5,
                      color: "var(--oh-text-secondary)" }}>
          <span>Client : {mission.tenant_id || "—"}</span>
          <span>Demandeur : {mission.owner || "—"}</span>
          <span>Créée le {dateFR(mission.created_at)}</span>
        </div>
      </div>

      {/* 🔑 LE POURQUOI D'ABORD. C'est la question qu'on se pose en ouvrant. */}
      {mission.pourquoi_bloque && (
        <div style={{ ...carte, background: "var(--oh-stat-red-bg)", marginBottom: 16,
                      display: "flex", gap: 10, alignItems: "flex-start" }}>
          <AlertTriangle size={16} style={{ color: "var(--oh-stat-red-text)",
                                            flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: "var(--oh-stat-red-text)", lineHeight: 1.6 }}>
            {mission.pourquoi_bloque}
          </div>
        </div>
      )}

      <div style={{ ...carte, marginBottom: 16 }}>
        <div style={{ ...etiquette, marginBottom: 10 }}>
          Travaux ({wi.length})
        </div>
        {wi.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--oh-text-muted)" }}>
            Aucun travail rattaché.
          </div>
        )}
        {wi.map((w) => (
          <div key={w.reference} style={{ display: "flex", gap: 12, alignItems: "center",
                                          padding: "8px 0", borderTop: "0.5px solid var(--oh-border)" }}>
            <span style={{ fontFamily: "monospace", fontSize: 13, color: "var(--oh-text)" }}>
              {w.reference}
            </span>
            <Badge statut={w.status} />
            <span style={{ fontSize: 12.5, color: "var(--oh-text-secondary)", marginLeft: "auto" }}>
              {w.completed_at ? `Clos le ${dateFR(w.completed_at)}` : "Non clos"}
            </span>
          </div>
        ))}
      </div>

      <div style={carte}>
        <div style={{ ...etiquette, marginBottom: 10 }}>
          Exécutions et preuves ({ex.length})
        </div>
        {ex.length === 0 && (
          <div style={{ fontSize: 13, color: "var(--oh-text-muted)" }}>
            Aucune exécution — la mission n'a pas encore été lancée.
          </div>
        )}
        {ex.map((e) => (
          <div key={e.id} style={{ padding: "10px 0", borderTop: "0.5px solid var(--oh-border)" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 13, color: "var(--oh-text)" }}>{e.agent}</span>
              <Badge statut={e.etat === "reussie" ? "terminee"
                            : e.etat === "echouee" ? "bloquee" : "en_cours"} />
              <span style={{ fontSize: 12.5, color: "var(--oh-text-secondary)" }}>
                sur {e.work_item}
              </span>
            </div>
            {/* ⚠️ La preuve est affichée BRUTE. ⛔ La résumer masquerait ce qui
                a réellement été mesuré — et c'est tout l'intérêt. */}
            {e.resultat ? (
              <pre style={{ margin: 0, fontSize: 11.5, lineHeight: 1.6, overflowX: "auto",
                            background: "var(--oh-bg-surface)", borderRadius: 8, padding: 10,
                            color: "var(--oh-text-secondary)" }}>
                {JSON.stringify(e.resultat, null, 2)}
              </pre>
            ) : (
              <div style={{ fontSize: 12.5, color: "var(--oh-text-muted)" }}>
                Aucune preuve enregistrée.
              </div>
            )}
          </div>
        ))}
      </div>

      {mission.statut !== "attente_humain" && (
        <>
          <button style={{ ...btnDiscret, marginTop: 16 }} disabled={occupe}
                  onClick={() => onEscalade(mission.id)}
                  aria-label="Marquer cette mission comme nécessitant une intervention humaine">
            {occupe ? <Loader2 size={14} className="animate-spin" /> : <Hand size={14} />}
            Marquer « attente humain »
          </button>
          <AvertissementHITL />
        </>
      )}
    </div>
  );
}

export default function MissionsAgentic() {
  const [missions, setMissions] = useState([]);
  const [detail, setDetail] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await fetch(BASE, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      // 🔑 Tri par défaut : la plus RÉCENTE en premier. ⛔ L'API trie déjà, on
      // ne s'y fie pas — un écran qui dépend d'un tri distant ment le jour où
      // il change.
      setMissions([...data].sort((a, b) =>
        String(b.created_at || "").localeCompare(String(a.created_at || ""))));
    } catch (e) {
      setErreur(String(e.message || e));
    } finally {
      setChargement(false);
    }
  }, []);

  const ouvrir = useCallback(async (id) => {
    setErreur(null);
    try {
      const r = await fetch(`${BASE}/${id}`, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDetail(await r.json());
    } catch (e) {
      setErreur(String(e.message || e));
    }
  }, []);

  const escalader = useCallback(async (id) => {
    setOccupe(true);
    try {
      const r = await fetch(`${BASE}/${id}/escalade`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await ouvrir(id);
      await charger();
    } catch (e) {
      setErreur(String(e.message || e));
    } finally {
      setOccupe(false);
    }
  }, [ouvrir, charger]);

  useEffect(() => { charger(); }, [charger]);

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 24, gap: 12 }}>
        <h1 style={{ fontSize: 20, fontWeight: 500, color: "var(--oh-text)", margin: 0 }}>
          Missions
        </h1>
        <button style={btnDiscret} onClick={charger} disabled={chargement}
                aria-label="Recharger les missions">
          {chargement ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          Rafraîchir
        </button>
      </div>

      {erreur && (
        <div style={{ ...carte, background: "var(--oh-stat-red-bg)", marginBottom: 16,
                      color: "var(--oh-stat-red-text)", fontSize: 13 }}>
          Impossible de charger les missions : {erreur}
        </div>
      )}

      {detail ? (
        <Detail mission={detail} occupe={occupe}
                onRetour={() => { setDetail(null); charger(); }}
                onEscalade={escalader} />
      ) : (
        <>
          <Compteurs missions={missions} />

          {chargement && missions.length === 0 && (
            <div style={{ ...carte, textAlign: "center", color: "var(--oh-text-muted)" }}>
              Chargement…
            </div>
          )}

          {!chargement && missions.length === 0 && !erreur && (
            <div style={{ ...carte, color: "var(--oh-text-secondary)", fontSize: 13,
                          lineHeight: 1.6 }}>
              Aucune mission pour l'instant. Une mission est créée puis lancée par
              l'orchestrateur ; elle apparaîtra ici dès sa création.
            </div>
          )}

          {missions.map((m) => (
            <button key={m.id} onClick={() => ouvrir(m.id)}
                    style={{ ...carte, width: "100%", marginBottom: 8, cursor: "pointer",
                             display: "flex", alignItems: "center", gap: 12,
                             textAlign: "left", font: "inherit" }}
                    aria-label={`Ouvrir la mission ${m.objectif}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, color: "var(--oh-text)", lineHeight: 1.6,
                              overflow: "hidden", textOverflow: "ellipsis",
                              whiteSpace: "nowrap" }}>
                  {m.objectif}
                </div>
                <div style={{ ...etiquette, marginTop: 4 }}>
                  {(m.work_items || []).length} travail(aux)
                  {m.tenant_id ? ` · ${m.tenant_id}` : ""}
                  {" · "}{dateFR(m.created_at)}
                  {m.besoin_hitl && m.statut !== "attente_humain" && (
                    <span style={{ color: "var(--oh-stat-purple-text)" }}>
                      {" · "}intervention prévue
                    </span>
                  )}
                </div>
              </div>
              <Badge statut={m.statut} />
              <ChevronRight size={16} style={{ color: "var(--oh-text-muted)", flexShrink: 0 }} />
            </button>
          ))}
        </>
      )}
    </div>
  );
}
